/**
 * CENTRO DE TRABAJO — F10.1
 *
 * Quien trabaja en un centro no debería elegir cliente y centro cada vez que abre «Hoy» o el
 * Planner. Reglas (cerradas):
 *
 *   · ENCARGADO con UN centro (`alcanceCentros` de una sola entrada): ese centro, sin preguntar.
 *   · Con varios (o ADMIN/CONSULTOR, sin recorte): el último elegido, recordado por usuario en
 *     este navegador — sólo si sigue dentro de su ámbito. Si no hay ninguno, se elige a mano.
 *
 * El recuerdo es una comodidad del navegador (localStorage), nunca autoridad: el backend sigue
 * acotando por ámbito cada petición (`centroAccesible`). Se lee y escribe dentro de try/catch —
 * un navegador sin almacenamiento simplemente no recuerda.
 */
import { useEffect, useRef, type ReactNode } from 'react';

import { api, type Centro, type Cliente } from '../api.ts';
import { useSesion } from '../contexto.tsx';
import { useDatos } from './base.tsx';

interface CentroRecordado {
  readonly idCliente: string;
  readonly idCentro: string;
}

function clave(idUsuario: string): string {
  return `neatos.centroTrabajo.${idUsuario}`;
}

export function leerCentroRecordado(idUsuario: string): CentroRecordado | null {
  try {
    const v = JSON.parse(localStorage.getItem(clave(idUsuario)) ?? 'null') as Partial<CentroRecordado> | null;
    return v !== null && typeof v.idCliente === 'string' && typeof v.idCentro === 'string' && v.idCentro !== ''
      ? { idCliente: v.idCliente, idCentro: v.idCentro }
      : null;
  } catch {
    return null;
  }
}

export function recordarCentro(idUsuario: string, idCliente: string, idCentro: string): void {
  try {
    localStorage.setItem(clave(idUsuario), JSON.stringify({ idCliente, idCentro }));
  } catch {
    //  Sin almacenamiento: no se recuerda, nada más.
  }
}

export interface CentroInicial {
  /** false mientras se resuelve el cliente del centro único. */
  readonly listo: boolean;
  readonly idCliente: string;
  readonly idCentro: string;
  /** ENCARGADO con un solo centro: no hay nada que elegir. */
  readonly unico: boolean;
  /** El centro único ya cargado (nombre y cliente para el contexto) — null si no es único. */
  readonly centroUnico: Centro | null;
}

/** El centro con el que arrancar una pantalla que no trae uno en la URL. */
export function useCentroInicial(): CentroInicial {
  const { usuario } = useSesion();
  const alcance = usuario?.alcanceCentros ?? null;
  const idUnico = alcance !== null && alcance.length === 1 ? alcance[0]! : null;
  const unico = useDatos(
    () => (idUnico === null ? Promise.resolve(null) : api.get<Centro>(`/api/centros/${idUnico}`)),
    [idUnico],
  );
  if (idUnico !== null) {
    const c = unico.datos;
    return { listo: c !== null || unico.error !== null, idCliente: c?.idCliente ?? '', idCentro: idUnico, unico: true, centroUnico: c };
  }
  const recordado = usuario === null ? null : leerCentroRecordado(usuario.id);
  const dentro = recordado !== null && (alcance === null || alcance.includes(recordado.idCentro));
  return { listo: true, idCliente: dentro ? recordado.idCliente : '', idCentro: dentro ? recordado.idCentro : '', unico: false, centroUnico: null };
}

/**
 * Aplica el centro inicial a una pantalla cuyo centro vive en la URL (`idCliente`/`idCentro`) y
 * recuerda cada elección posterior. Una sola vez al llegar sin centro: si el usuario luego lo
 * vacía a propósito, no se le vuelve a imponer.
 */
export function useCentroEnUrl(
  idCliente: string,
  idCentro: string,
  fijar: (cambios: { idCliente: string; idCentro: string }) => void,
): CentroInicial {
  const { usuario } = useSesion();
  const inicial = useCentroInicial();
  //  Pre-F14.2 (bug del VPS): antes el efecto dependía de «el centro está vacío», y cambiar de
  //  cliente lo vacía — así que, en cuanto había un centro recordado, cada cambio de cliente
  //  volvía a imponerlo CON SU CLIENTE y el selector saltaba de vuelta. Ahora se decide UNA sola
  //  vez por visita, al resolverse el inicial: sólo si se llegó sin centro y sin otro cliente.
  const decidido = useRef(false);
  const llegada = useRef({ idCliente, idCentro });
  useEffect(() => {
    //  Sin sesión todavía no se sabe qué recordaba este usuario: se espera, no se decide.
    if (decidido.current || !inicial.listo || usuario === null) return;
    decidido.current = true;
    const { idCliente: clienteLlegada, idCentro: centroLlegada } = llegada.current;
    if (centroLlegada !== '' || inicial.idCentro === '' || inicial.idCliente === '') return;
    if (clienteLlegada !== '' && clienteLlegada !== inicial.idCliente) return;
    fijar({ idCliente: inicial.idCliente, idCentro: inicial.idCentro });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inicial.listo, usuario]);
  useEffect(() => {
    if (usuario !== null && idCliente !== '' && idCentro !== '') recordarCentro(usuario.id, idCliente, idCentro);
  }, [usuario, idCliente, idCentro]);
  return inicial;
}

/**
 * Selector cliente → centro (el habitual de ADMIN/CONSULTOR y del ENCARGADO con varios centros;
 * el backend ya recorta ambas listas a su ámbito). Con un centro único no hay selector: sólo el
 * contexto, en texto.
 */
export function SelectorCentroTrabajo({
  idPrefijo,
  idCliente,
  idCentro,
  inicial,
  fijar,
}: {
  idPrefijo: string;
  idCliente: string;
  idCentro: string;
  inicial: CentroInicial;
  fijar: (cambios: { idCliente: string; idCentro: string }) => void;
}): ReactNode {
  //  Con centro único no se pide ninguna lista: el contexto sale del centro ya cargado.
  const unico = inicial.unico;
  const clientes = useDatos(
    () => (unico ? Promise.resolve({ clientes: [] as Cliente[] }) : api.get<{ clientes: Cliente[] }>('/api/clientes')),
    [unico],
  );
  const centros = useDatos(
    () => (unico || idCliente === '' ? Promise.resolve({ centros: [] as Centro[] }) : api.get<{ centros: Centro[] }>(`/api/clientes/${idCliente}/centros`)),
    [unico, idCliente],
  );

  if (unico) {
    const c = inicial.centroUnico;
    return (
      <div className="dato">
        <div className="etiqueta-dato">Centro</div>
        <div className="valor-dato">{c === null ? '…' : `${c.descripcion} · ${c.clienteRazonSocial}`}</div>
      </div>
    );
  }
  return (
    <>
      <div className="campo">
        <label htmlFor={`${idPrefijo}-cliente`}>Cliente</label>
        <select id={`${idPrefijo}-cliente`} value={idCliente} onChange={(e) => fijar({ idCliente: e.target.value, idCentro: '' })}>
          <option value="">Elige un cliente…</option>
          {(clientes.datos?.clientes ?? []).map((c) => (
            <option key={c.id} value={c.id}>{c.razonSocial}</option>
          ))}
        </select>
      </div>
      <div className="campo">
        <label htmlFor={`${idPrefijo}-centro`}>Centro</label>
        <select
          id={`${idPrefijo}-centro`}
          value={idCentro}
          disabled={idCliente === ''}
          onChange={(e) => fijar({ idCliente, idCentro: e.target.value })}
        >
          <option value="">Elige un centro…</option>
          {(centros.datos?.centros ?? []).map((c) => (
            <option key={c.id} value={c.id}>{c.descripcion}</option>
          ))}
        </select>
      </div>
    </>
  );
}
